import { ReactNode, Suspense, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { enableMocking } from './api/mocks'
import { Guard } from './auth/Guard'
import { ErrorBoundaryErrorPage } from './components/Shared/CustomErrorComponent'
import { Skeleton } from './components/Skeleton'
import { VITE_GIT_COMMIT } from './config/build_vars'
import './global.css'
import { initializeConfigService } from './initConfigService'
import { routes } from './routes'
import { EventStreamProvider } from './support/EventStreamProvider'
import { VendorAddons } from './support/VendorAddons'
import { logger } from './utils/logging'
import { initMixpanel, mixpanel } from './support/mixpanel'
import { useEntitlements } from './hooks/useEntitlements'
import '@flatfile/baseline/styles.css'

export async function main() {
  initMixpanel()

  // Mark the start time as soon as this file loads
  performance.mark('app_start')

  const router = createBrowserRouter(routes, {
    basename: window.flatfile.configService.get('VITE_DASHBOARD_BASEPATH'),
  })

  // NOTE: this one we don't get from ConfigService, it is really truly a build var, not a config var
  if (VITE_GIT_COMMIT) {
    // eslint-disable-next-line no-console
    console.log(`Flatfile version ${VITE_GIT_COMMIT}`)
    logger.setContext({
      version: VITE_GIT_COMMIT,
    })
  }

  enableMocking().then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <VendorAddons>
        <Suspense fallback={<Skeleton name='Loading Resources...' />}>
          <Guard>
            <EventStreamProvider>
              <ErrorBoundary
                fallbackRender={({ error }) => <ErrorBoundaryErrorPage page='main.tsx' error={error} logout />}
              >
                <MixpanelEnabler>
                  <RouterProvider router={router} />
                </MixpanelEnabler>
              </ErrorBoundary>
            </EventStreamProvider>
          </Guard>
        </Suspense>
      </VendorAddons>
    )
  })
}

initializeConfigService()
  .then(() => {
    main()
  })
  .catch((error: Error) => {
    console.error('Error initializing config service:', error)
  })

function MixpanelEnabler({ children }: { children: ReactNode }) {
  const { hasEntitlement } = useEntitlements()
  const mixpanelEnabled = hasEntitlement('mixpanelAnalytics')

  useEffect(() => {
    if (mixpanelEnabled) {
      mixpanel.opt_in_tracking({
        track: () => {}, // we do this to avoid an automatic $opt_in event being tracked
      })
    }
  }, [mixpanelEnabled])

  return <>{children}</>
}
